import type { TagSortOrder } from '@mnd-frontend/graphql-schema';
import { Chip, Dialog, Input, SPACING_1, useToasts } from '@mnd-frontend/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useTagCombineMutation, useTagNamesQuery } from '../../generated/hooks';

const TagsWrapper = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: ${SPACING_1};
`;

const CombineTagsModal = ({
  onClose,
  onSave,
  page,
  perPage,
  query,
  sortOrder,
  tagIds,
  pressroomId,
}: {
  onClose: () => void;
  onSave: () => void;
  page: number;
  perPage: number;
  query: string;
  sortOrder: TagSortOrder;
  tagIds: string[];
  pressroomId: string;
}) => {
  const [tagName, setTagName] = useState('');
  const [tagNameError, setTagNameError] = useState<string | undefined | null>(undefined);
  const [t] = useTranslation();
  const [combineTag] = useTagCombineMutation();
  const { showSuccessToast, showFailureToast } = useToasts();
  const { data, loading } = useTagNamesQuery({
    variables: {
      pressroomId,
      ids: tagIds,
    },
  });
  const tags = data?.pressroom?.tags2.hits ?? [];

  return (
    <Dialog
      description={t('category_management_combine_heading')}
      open={!loading}
      confirmAction={{
        onClick: async () => {
          try {
            const result = await combineTag({
              variables: {
                ids: tagIds,
                name: tagName,
                page,
                perPage,
                sortOrder,
                query,
                pressroomId,
              },
            });
            const error = result.data?.tagCombine?.error;
            if (!error) {
              onSave();
              showSuccessToast(
                t('category_management_combine_categories_success', { categoryName: tagName }),
              );
              return;
            }

            if (error?.__typename === 'TagCombineValidationError') {
              setTagNameError(error.name);
            } else if (error?.message) {
              showFailureToast(error.message);
            } else {
              showFailureToast(t('common_general_error'));
            }
          } catch (err) {
            showFailureToast(t('common_general_error'));
          }
        },
      }}
      onClose={() => onClose()}
    >
      <TagsWrapper>
        {tags.map(tag => (
          <li key={tag.id}>
            <Chip type="readOnly">{tag.name}</Chip>
          </li>
        ))}
      </TagsWrapper>
      <Input
        id="tagName"
        label={t('category_management_combine_input_label')}
        error={tagNameError}
        value={tagName}
        onChange={e => setTagName(e.target.value)}
      />
    </Dialog>
  );
};

export default CombineTagsModal;
